import { useEffect, useState } from 'react';

import { formatKES } from '../../lib/format';

/**
 * Dual-thumb price range slider for the catalog filters (SRS §4.3). The domain comes from
 * the live catalog facets; the two thumbs never cross, and the selected range is shown as a
 * label beneath. Dragging updates the visual live but only commits (fires onChange → URL →
 * refetch) on release, so the grid isn't re-queried on every pixel.
 */
export function PriceRangeSlider({
  bounds,
  value,
  onChange,
}: {
  bounds: { min: number; max: number };
  /** Current selection; falls back to the full domain when a side is unset. */
  value: { min?: number; max?: number };
  onChange: (next: { min: number; max: number }) => void;
}): JSX.Element {
  const domainMin = bounds.min;
  const domainMax = Math.max(bounds.max, bounds.min + 1); // guard against a zero-width domain
  const step = niceStep(domainMax - domainMin);

  const [lo, setLo] = useState(clamp(value.min ?? domainMin, domainMin, domainMax));
  const [hi, setHi] = useState(clamp(value.max ?? domainMax, domainMin, domainMax));

  // Re-sync when the URL-driven value or the domain (category switch) changes.
  useEffect(() => {
    setLo(clamp(value.min ?? domainMin, domainMin, domainMax));
    setHi(clamp(value.max ?? domainMax, domainMin, domainMax));
  }, [value.min, value.max, domainMin, domainMax]);

  const pct = (v: number) => ((v - domainMin) / (domainMax - domainMin)) * 100;
  // Commit from explicit values so the URL write never races a pending state update.
  const commit = (nextLo: number, nextHi: number) => onChange({ min: nextLo, max: nextHi });

  return (
    <div>
      <div className="dual-range relative h-5">
        {/* Track */}
        <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-frost" />
        {/* Selected segment */}
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-sky-600"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        <input
          type="range"
          min={domainMin}
          max={domainMax}
          step={step}
          value={lo}
          aria-label="Minimum price"
          onChange={(e) => setLo(Math.min(Number(e.target.value), hi))}
          onMouseUp={() => commit(lo, hi)}
          onTouchEnd={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
        />
        <input
          type="range"
          min={domainMin}
          max={domainMax}
          step={step}
          value={hi}
          aria-label="Maximum price"
          onChange={(e) => setHi(Math.max(Number(e.target.value), lo))}
          onMouseUp={() => commit(lo, hi)}
          onTouchEnd={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="font-medium text-navy-900">{formatKES(String(lo))}</span>
        <span className="text-neutral">–</span>
        <span className="font-medium text-navy-900">{formatKES(String(hi))}</span>
      </div>
    </div>
  );
}

const clamp = (n: number, min: number, max: number): number => Math.min(Math.max(n, min), max);

/** A round step so both the thumbs and the readout land on tidy values. */
function niceStep(range: number): number {
  if (range >= 50_000) return 500;
  if (range >= 10_000) return 100;
  if (range >= 2_000) return 50;
  if (range >= 500) return 10;
  return 1;
}
